/** What a revenue schedule defers. Mirrors RevenueScheduleKind in the API. */
export type RevenueScheduleKind = "cycle" | "upgrade_charge" | "upgrade_credit";

export const REVENUE_SCHEDULE_KIND_LABEL: Record<RevenueScheduleKind, string> = {
  cycle: "Cycle",
  upgrade_charge: "Upgrade charge",
  upgrade_credit: "Upgrade credit",
};

export type RevenueScheduleStatus = "active" | "completed";

export type RollForwardMonthStatus = "closed" | "open" | "in_progress" | "upcoming";

/** One month of the 24100 roll-forward. Figures are null for a future month with no activity. */
export interface RollForwardMonth {
  period: string;
  status: RollForwardMonthStatus;
  opening: string | null;
  added: string | null;
  recognised: string | null;
  closing: string | null;
  /** Lines for this month not yet posted. */
  scheduled: string;
}

export interface RollForward {
  fiscalYear: number;
  asOf: string;
  months: RollForwardMonth[];
  totals: {
    opening: string;
    added: string;
    recognised: string;
    closing: string;
    scheduled: string;
  };
}

export interface OrgDeferralRow {
  organizationId: number;
  organizationName: string;
  planName: string | null;
  billingInterval: string | null;
  cycles: number;
  opening: string;
  added: string;
  recognised: string;
  closing: string;
  nextPeriod: string | null;
  nextAmount: string | null;
}

export interface OrgDeferralTotals {
  organizations: number;
  cycles: number;
  opening: string;
  added: string;
  recognised: string;
  closing: string;
}

export interface OrgDeferralPage {
  data: OrgDeferralRow[];
  total: number;
  totalPages: number;
  totals: OrgDeferralTotals;
}

export type OrgDeferralSortKey = "name" | "opening" | "added" | "recognised" | "closing";

export interface OrgDeferralsQuery {
  fy: number;
  search?: string;
  sortBy?: OrgDeferralSortKey;
  sortDir?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface ScheduleLineDetail {
  period: string;
  days: number;
  amount: string;
  journalEntryId: number | null;
  journalNo: string | null;
  postedOn: string | null;
  dueOn: string;
}

export interface ScheduleCycleDetail {
  scheduleId: number;
  renewalId: number;
  sequence: number;
  kind: RevenueScheduleKind;
  serviceStart: string;
  serviceEnd: string;
  stripeInvoiceNumber: string | null;
  hostedInvoiceUrl: string | null;
  amount: string;
  recognised: string;
  remaining: string;
  status: RevenueScheduleStatus;
  lines: ScheduleLineDetail[];
}

export interface OrgScheduleDetail {
  organizationId: number;
  organizationName: string;
  planName: string | null;
  billingInterval: string | null;
  fiscalYear: number;
  remaining: string;
  recognisedInYear: string;
  nextPeriod: string | null;
  nextAmount: string | null;
  cycles: ScheduleCycleDetail[];
}

export interface UnscheduledPayment {
  journalEntryId: number;
  journalNo: string;
  renewalId: number | null;
  organizationName: string | null;
  stripeInvoiceNumber: string | null;
  cycleStart: string | null;
  cycleEnd: string | null;
  paidOn: string;
  amount: string;
}

export interface UnexplainedEntry {
  journalEntryId: number;
  journalNo: string;
  accountingDate: string;
  reasonCode: string;
  memo: string | null;
  amount: string;
}

export interface ScheduleReconciliation {
  asOf: string;
  scheduleRemaining: string;
  ledgerBalance: string;
  difference: string;
  explained: string;
  paymentsWithoutSchedule: UnscheduledPayment[];
  unexplainedEntries: UnexplainedEntry[];
}

export type RecognitionRunStatus = "running" | "succeeded" | "failed" | "missed";

export interface RecognitionRunRow {
  id: number;
  scheduledFor: string;
  status: RecognitionRunStatus;
  startedAt: string;
  finishedAt: string | null;
  postedCount: number;
  failedCount: number;
  stillDueCount: number;
  error: string | null;
  triggeredBy: string | null;
}

export interface RecognitionRunState {
  scheduledFor: string;
  state: RecognitionRunStatus | "due";
  run: RecognitionRunRow | null;
  backlogCount: number;
  backlogAmount: string;
  canRerun: boolean;
  nextScheduledFor: string;
}

export interface RecognitionRunsResponse {
  state: RecognitionRunState;
  data: RecognitionRunRow[];
  total: number;
  totalPages: number;
}

export interface UnpostedReleases {
  through: string;
  count: number;
  amount: string;
  earliestPeriod: string | null;
}
